// src/components/LogoCarousel.js

import React, { useMemo, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const translations = {
    en: {
        title: "Successful Partners",
        description: "We've proudly collaborated with industry leaders to create exceptional digital experiences.",
    },
    ar: {
        title: "شركاء النجاح",
        description: "لقد تعاونا بفخر مع قادة الصناعة لخلق تجارب رقمية استثنائية.",
    },
};

const LogoCarousel = () => {
    const { language } = useLanguage();
    const t = translations[language] || translations['en'];

    // --- MODIFICATION 1: Identify special logos by filename ---
    // You can easily add or remove filenames here to change which logos are bigger.
    const specialLogos = ['2.svg', '10.svg'];

    const logos = useMemo(() => {
        const logoContext = require.context(
            '../assets/images/new/logos',
            false,
            /\.(png|jpe?g|svg)$/i
        );
        const customSort = (a, b) => {
            const numA = parseInt(a.match(/(\d+)/)?.[1] || 0);
            const numB = parseInt(b.match(/(\d+)/)?.[1] || 0);
            const isAPriority = numA >= 21 && numA <= 28;
            const isBPriority = numB >= 21 && numB <= 28;
            if (isAPriority && !isBPriority) return -1;
            if (!isAPriority && isBPriority) return 1;
            return numA - numB;
        };

        // We now map over the keys to create an array of objects.
        // Each object contains the path and whether it's a special logo.
        return logoContext.keys().sort(customSort).map(key => {
            // Extract the filename from the key (e.g., './2.svg' -> '2.svg')
            const filename = key.split('/').pop();
            return {
                path: logoContext(key),
                isSpecial: specialLogos.includes(filename),
                originalKey: key // for a stable key prop
            };
        });
    }, []);

    const duplicatedLogos = [...logos, ...logos];

    useEffect(() => {
        const logoCount = logos.length;
        if (logoCount > 0) {
            document.documentElement.style.setProperty('--logo-count', logoCount.toString());
            return () => {
                document.documentElement.style.removeProperty('--logo-count');
            };
        }
    }, [logos.length]);

    return (
        <div className="py-16 bg-light md:pl-48">
            <div className="container mx-auto px-4">
                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-greycliff font-bold mb-4 text-neutral-900">{t.title}</h2>
                    <p className="text-dark/80 max-w-2xl mx-auto">
                        {t.description}
                    </p>
                </div>
            </div>

            <div
                className="
                    relative w-full overflow-hidden grid place-items-center
                    h-[--slider-height-mobile] md:h-[--slider-height-desktop]
                    before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-[15%] before:bg-gradient-to-r before:from-light before:to-transparent
                    after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-[15%] after:bg-gradient-to-l after:from-light after:to-transparent
                "
            >
                <div
                    className="
                        flex items-center  /* We align items here for the scaling effect */
                        animate-scroll-mobile md:animate-scroll-desktop
                        w-[calc(var(--logo-width-mobile)*var(--logo-count)*2)] md:w-[calc(var(--logo-width-desktop)*var(--logo-count)*2)]
                    "
                >
                    {/* --- MODIFICATION 2: Use the new `isSpecial` property --- */}
                    {duplicatedLogos.map(({ path, isSpecial, originalKey }, index) => {
                        // Apply conditional classes using the `isSpecial` boolean.
                        // We use `scale-125` to make it 25% larger.
                        const scaleClass = isSpecial ? 'scale-125' : 'scale-100';

                        return (
                            <div
                                // The key is now more robust for the duplicated array
                                key={`${originalKey}-${index}`}
                                className={`
                                    flex items-center justify-center px-4 transition-transform duration-300
                                    h-[--logo-height-mobile] w-[--logo-width-mobile]
                                    md:h-[--logo-height-desktop] md:w-[--logo-width-desktop]
                                    ${scaleClass}
                                `}
                            >
                                <img
                                    src={path}
                                    alt={`Partner logo ${index}`}
                                    className="block max-w-full max-h-full object-contain"
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default LogoCarousel;